import { parseMultipleParamValue } from '@/helpers/destinations/destination-request-helper'
import { PARAMETERS_TYPES } from '@/constants/parametersTypes'

function buildCallFlowValue(blockName, paramName) {
    return `${PARAMETERS_TYPES.callFlow}:${blockName}:${paramName}`
}

function parseParamValueParts(value) {
    const parts = value.split(':').map((part) => part.trim())
    const type = parts[0]

    if (!type) {
        return null
    }

    return {
        type,
        blockName: parts.length >= 3 ? parts[1] : null,
        paramName: parts.length >= 3 ? parts[2] : parts[1],
    }
}

function rebuildParamValue(value, mapper) {
    const values = parseMultipleParamValue(value)
    const list = values.length ? values : [value]
    const updated = list.map(mapper)

    return values.length ? `[${updated.join('|')}]` : updated[0]
}

function shouldUpdateCallerInputParamValue(parsed, oldParamName) {
    if (!parsed) {
        return false
    }

    const { type, paramName } = parsed
    const isCallerInputType = type === PARAMETERS_TYPES.callerInputParameters
    const isCallFlowType = type === PARAMETERS_TYPES.callFlow

    return (isCallerInputType || isCallFlowType) && paramName === oldParamName
}

function shouldUpdateCallerInputBlockNameValue(parsed, oldBlockName, parameterName) {
    if (!parsed) {
        return false
    }

    const { type, blockName, paramName } = parsed
    const isCallerInputType = type === PARAMETERS_TYPES.callerInputParameters
    const isCallFlowType = type === PARAMETERS_TYPES.callFlow

    if (isCallerInputType && blockName === oldBlockName && paramName === parameterName) {
        return true
    }

    if (isCallerInputType && !blockName && paramName === parameterName) {
        return true
    }

    return isCallFlowType && blockName === oldBlockName && paramName === parameterName;
}

function shouldRemoveCallerInputValue(parsed, parameterName) {
    if (!parsed) {
        return false
    }

    const { type, paramName } = parsed
    const isCallerInputType = type === PARAMETERS_TYPES.callerInputParameters
    const isCallFlowType = type === PARAMETERS_TYPES.callFlow

    return (isCallerInputType || isCallFlowType) && paramName === parameterName
}

/**
 * Рассчитывает расстояние между двумя точками.
 * @param {Object} start - Начальная точка.
 * @param {Object} end - Конечная точка.
 * @returns {number} - Расстояние между точками.
 */
function distance(start, end) {
    const dx = start.x - end.x
    const dy = start.y - end.y
    return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Генерирует путь для плавной кривой Безье между двумя точками.
 * @param {Object} positionStart - Объект с координатами и размерами начальной точки.
 * @param {Object} positionEnd - Объект с координатами и размерами конечной точки.
 * @returns {Object} - Объект с путем для атрибута d элемента <path> и центральной точкой.
 */
function getRoundedPath(positionStart, positionEnd) {
    const startX = positionStart.x
    const startY = positionStart.y
    const endX = positionEnd.x
    const endY = positionEnd.y
    const startWidth = positionStart.width
    const startHeight = positionStart.height
    const endWidth = positionEnd.width
    const endHeight = positionEnd.height

    // Координаты центров начального и конечного блоков
    const start = { x: startX + startWidth / 2, y: startY + startHeight / 2 }
    const end = { x: endX + endWidth / 2, y: endY + endHeight / 2 }

    // Координаты средней точки между началом и концом
    const center = {
        x: (start.x + end.x) / 2,
        y: (start.y + end.y) / 2,
    }

    // Контрольная точка для кривой Безье
    const controlPoint = {
        x: start.x + Math.min(distance(start, end), Math.abs(end.y - start.y) / 2, 150),
        y: start.y,
    }

    // Генерация пути для плавной кривой
    const path = `
        M ${start.x},${start.y}
        Q ${controlPoint.x}, ${controlPoint.y} ${center.x},${center.y}
        T ${end.x},${end.y}
    `

    return { path, center }
}

export {
    getRoundedPath,
    buildCallFlowValue,
    parseParamValueParts,
    rebuildParamValue,
    shouldUpdateCallerInputParamValue,
    shouldUpdateCallerInputBlockNameValue,
    shouldRemoveCallerInputValue,
}
